'use client';

import './project-mobile-detail.scss';
import { WarningOutlined } from "@ant-design/icons";
import { ProjectModel } from "@/lib/models/Project";
import ImageElement from '@/components/elements/ImageElement';
import MobileNavigationBar from '@/components/Mobile/MobileNavigationBar';
import { useContext, useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { RouterName } from '@/lib/enum/RouterEnum';
import { setNavState } from '@/lib/utils/navState';
import { PAGE_MOBILE } from '@/lib/constants';
import { useTransitionExit } from '@/components/Mobile/Transition/SwipeTransition';
import TransitionContext from '@/components/Mobile/Transition/TransitionContext';
import VideoElement from '@/components/elements/VideoElement';

interface Props {
  project: ProjectModel;
  projects: ProjectModel[];
}

const ProjectMobileDetail = ({ project, projects }: Props) => {
  const swipeDownLength = 150;
  const pathname = usePathname();
  const { markRouteReady } = useContext(TransitionContext);

  useEffect(() => {
    markRouteReady(pathname);
  }, [pathname, markRouteReady]);

  const exit = useTransitionExit();

  // Handle swipe navigation
  const touchStartYRef = useRef(0);
  const touchEndYRef = useRef(0);
  const projectRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0].clientY;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndYRef.current = e.changedTouches[0].clientY;
    handleSwipe();
  };

  const handleSwipe = () => {
    const swipeDistance = touchStartYRef.current - touchEndYRef.current;
    const body = projectRef.current;
    if (body) {
      const atTopOfScroll = body.scrollTop <= 0;
      if (atTopOfScroll && swipeDistance < -swipeDownLength) {
        goBack();
      }
    }
  };

  const goBack = () => {
    const projectIdx = projects.findIndex(p => p.id === project.id);
    if (projectIdx > -1) setNavState(projectIdx + PAGE_MOBILE.PROJECTS);
    exit(`${RouterName.dashboard}`);
  };

  const _renderImage = (link: string, index: number) => {
    return (
      <div className="project-image-container" key={index}>
        <ImageElement
          src={link}
          className='project-image'
          alt={""}
          scroller={projectRef}
          priority={index === 0}
        />
      </div>
    )
  };

  const _renderVideo = (link: string, index: number) => {
    return (
      <div className='project-image-container' key={index}>
        <VideoElement
          src={link}
          className='project-image'
          scroller=".project-mobile-detail"
        />
      </div>
    );
  };

  return (
    <>
      <div className={'project-mobile-detail'}
        ref={projectRef}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className={'project-description-container'} id={'header-project-detail-mobile'}>
          <p className={'text-experiment'}>EXPERIMENT</p>
          <p className={'text-name-project'}>{project?.projectTitle}</p>
          <p className={'text-description-project'}>{project?.projectDescription}</p>
        </div>
        {
          // Render project images and videos...
          project && project.projectMedia && project.projectMedia.map((item, index) => {
            if (item.includes('.mp4') || item.includes('.webm')) {
              return _renderVideo(item, index)
            } else {
              return _renderImage(item, index)
            }
          })
        }

        <div className='end-project-item'>
          <WarningOutlined className='icon-warning' />
          End of Experiment
        </div>
      </div>
      <MobileNavigationBar onBack={goBack} />
    </>
  )
};

export default ProjectMobileDetail;
